import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { disclaimerConfig, type DisclaimerConfig } from '../config/disclaimerConfig';

export const DataDisclaimerPage = () => {
  const [agreed, setAgreed] = useState(false);
  const [config, setConfig] = useState<DisclaimerConfig>(disclaimerConfig);
  const navigate = useNavigate();

  // Future: Load config from backend API
  useEffect(() => {
    // fetchDisclaimerConfig().then(setConfig);
  }, []);

  const handleProceed = () => {
    if (agreed) {
      // Store agreement in localStorage
      localStorage.setItem('data_disclaimer_agreed', 'true');
      localStorage.setItem('data_disclaimer_agreed_date', new Date().toISOString());
      // Force page reload to refresh App.tsx routing logic
      window.location.href = '/';
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          marginBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <WarningIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              Data Disclaimer & Terms of Use
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please read and acknowledge the following important information before proceeding
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Data Freshness Section */}
          <Alert severity="warning" icon={<ScheduleIcon />} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              ⚠️ Data Freshness & Limitations
            </Typography>
            <Typography variant="body2" component="div">
              All metrics and analytics in this platform are <strong>delayed and NOT real-time</strong>:
              <Box component="ul" sx={{ mt: 1, mb: 1 }}>
                <li><strong>Google Search Console (GSC):</strong> {config.dataDelays.gsc}</li>
                <li><strong>Google Analytics 4 (GA4):</strong> {config.dataDelays.ga4}</li>
                <li><strong>Bing Webmaster Tools:</strong> {config.dataDelays.bing}</li>
              </Box>
              The data shown represents historical patterns and <strong>may not reflect today's actual performance</strong>.
            </Typography>
          </Alert>

          {/* Terms Content */}
          <Stack spacing={2} sx={{ mb: 3 }}>
            {config.sections.map((section, index) => (
              <Box key={index}>
                <Typography variant="h6" gutterBottom>
                  {section.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  {section.content.map((item, i) => (
                    <span key={i}>
                      • {item}<br />
                    </span>
                  ))}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Agreement Checkbox */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I have read and understand the data limitations, risks, and terms of use. 
                  I agree to use this platform responsibly and acknowledge that all data is delayed and may not be 100% accurate.
                </Typography>
              }
            />
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                // Logout and return to login
                localStorage.removeItem('auth-storage');
                navigate('/login');
              }}
            >
              Decline & Logout
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleProceed}
              disabled={!agreed}
              startIcon={<CheckCircleIcon />}
              size="large"
            >
              I Agree & Proceed
            </Button>
          </Stack>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {config.lastUpdated} • Version {config.version}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default DataDisclaimerPage;
